import React, { useEffect, useState } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Text } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';
import TokenStatusIndicator from '../../common/TokenStatusIndicator';

export function NetsuiteM2MModal({ open, onClose, onSaved, config = {} }) {
  const [form, setForm] = useState({
    accountId: '',
    clientId: '',
    certificateId: '',
    privateKey: ''
  });
  const [saving, setSaving] = useState(false);
  const [valid, setValid] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        accountId: config.accountId || '',
        clientId: config.clientId || '',
        certificateId: config.certificateId || '',
        privateKey: config.privateKey === '****' ? '****' : (config.privateKey || '')
      });
      setValid(null);
    }
  }, [open, config]);

  const canSave = Boolean(
    form.accountId &&
    form.clientId &&
    form.certificateId &&
    ((form.privateKey && form.privateKey !== '****') || config.privateKey === '****')
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const credentials = {
        accountId: form.accountId,
        clientId: form.clientId,
        certificateId: form.certificateId
      };
      if (form.privateKey && form.privateKey !== '****') {
        credentials.privateKey = form.privateKey;
      }
      const resp = await configApi.saveNetsuiteM2M(credentials);
      if (resp?.success) {
        setValid(true);
        onSaved?.(resp);
        onClose?.();
      } else {
        setValid(false);
      }
    } catch (_) {
      setValid(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="NetSuite M2M (Client Credentials)"
      primaryAction={{ content: 'Save Credentials', onAction: handleSave, disabled: !canSave, loading: saving }}
      secondaryActions={[
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" fontWeight="bold">Client Credentials (M2M)</Text>
            {valid === true && <Badge tone="success">Saved</Badge>}
            {valid === false && <Badge tone="critical">Failed</Badge>}
          </InlineStack>
          <TextField
            label="Account ID"
            value={form.accountId}
            onChange={(v) => setForm({ ...form, accountId: v })}
            autoComplete="off"
            helpText="e.g. 1234567 or 1234567_SB1 for sandbox"
          />
          <TextField
            label="Client ID"
            value={form.clientId}
            onChange={(v) => setForm({ ...form, clientId: v })}
            autoComplete="off"
            helpText="Client ID from your NetSuite OAuth 2.0 Client Credentials integration"
          />
          <TextField
            label="Certificate ID"
            value={form.certificateId}
            onChange={(v) => setForm({ ...form, certificateId: v })}
            autoComplete="off"
            helpText="Certificate ID (kid) from OAuth 2.0 Client Credentials M2M Setup"
          />
          <TextField
            label="Private Key (PEM)"
            value={form.privateKey}
            onChange={(v) => setForm({ ...form, privateKey: v })}
            autoComplete="off"
            multiline={4}
            helpText={form.privateKey === '****' ? 'Stored; leave as **** to keep unchanged' : 'Paste the contents of private_key.pem (-----BEGIN EC PRIVATE KEY----- ...)'}
          />

          <div style={{ marginTop: '20px' }}>
            <Text variant="headingSm" fontWeight="bold">M2M Access Token Status</Text>
            <TokenStatusIndicator provider="netsuite" />
          </div>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}

export default NetsuiteM2MModal;
